import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";
import { armarFondo } from "../utils/fondoServicio";
import { colorTextoLegible } from "../utils/contraste";
import catalogoTemas, { TEMA_NINGUNO } from "../temas/catalogoTemas";
import catalogoEstilos from "../temas/catalogoEstilos";

const ESTILO_NINGUNO = { key: "ninguno", nombre: "Ninguno" };

const GestionarTemas = ({ setColorFondoGlobal }) => {
  const [actual, setActual] = useState(null);
  const [aplicando, setAplicando] = useState(null);
  const [estiloGeneral, setEstiloGeneral] = useState("clasico");
  const [estiloPantallaServicios, setEstiloPantallaServicios] = useState("clasico");
  const [aplicandoEstilo, setAplicandoEstilo] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [destinoEstilo, setDestinoEstilo] = useState("general");

  const cargarActual = async () => {
    try {
      const [resFondo, resSitio] = await Promise.all([
        axios.get(`${API_URL}/api/color-fondo`),
        axios.get(`${API_URL}/api/colores-sitio`),
      ]);
      setActual({
        fondo_app: resFondo.data.fondo_app,
        fondo_difuminado: !!resFondo.data.fondo_difuminado,
        color_base: resSitio.data.color_base,
        header_difuminado: !!resSitio.data.header_difuminado,
      });
      setEstiloGeneral(resSitio.data.estilo_visual || "clasico");
      setEstiloPantallaServicios(resSitio.data.estilo_servicios || "clasico");
    } catch (err) {
      console.error("Error cargando configuración actual:", err);
    }
  };

  const cargarServicios = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/servicios`);
      setServicios(res.data || []);
    } catch (err) {
      console.error("Error cargando servicios:", err);
    }
  };

  useEffect(() => {
    cargarActual();
    cargarServicios();
  }, []);

  // Estilo aplicado ahora mismo al destino elegido (general, la pantalla
  // de servicios, o un servicio puntual). Para un servicio, null significa
  // "sin estilo propio — usa el general".
  const estiloActivoDestino =
    destinoEstilo === "general"
      ? estiloGeneral
      : destinoEstilo === "pantalla_servicios"
      ? estiloPantallaServicios
      : servicios.find((s) => String(s.id) === String(destinoEstilo))?.estilo_visual || null;

  const nombreDestinoActual =
    destinoEstilo === "general"
      ? "los servicios sin estilo propio (general)"
      : destinoEstilo === "pantalla_servicios"
      ? "la pantalla de Selección de Servicio"
      : servicios.find((s) => String(s.id) === String(destinoEstilo))?.nombre || "este servicio";

  const aplicarEstilo = async (estilo) => {
    const esDesactivar = estilo.key === "ninguno";
    const esGeneral = destinoEstilo === "general";
    const esPantallaServicios = destinoEstilo === "pantalla_servicios";

    if (
      !window.confirm(
        esDesactivar
          ? esGeneral
            ? "¿Desactivar el estilo visual general y volver al look clásico por defecto?"
            : esPantallaServicios
            ? "¿Volver la pantalla de Selección de Servicio al look clásico?"
            : `¿Quitar el estilo propio de "${nombreDestinoActual}"? Volverá a usar el estilo general.`
          : `¿Aplicar el estilo "${estilo.nombre}" a "${nombreDestinoActual}"?`
      )
    )
      return;

    try {
      setAplicandoEstilo(estilo.key);

      if (esGeneral) {
        await axios.post(`${API_URL}/api/colores-sitio/estilo`, { estilo_visual: estilo.key });
      } else if (esPantallaServicios) {
        await axios.post(`${API_URL}/api/colores-sitio/estilo-servicios`, {
          estilo_visual: esDesactivar ? "clasico" : estilo.key,
        });
      } else {
        await axios.post(`${API_URL}/api/servicios/${destinoEstilo}/estilo`, {
          estilo_visual: esDesactivar ? null : estilo.key,
        });
      }

      await cargarActual();
      await cargarServicios();
      alert(
        esDesactivar
          ? `✅ Estilo quitado de "${nombreDestinoActual}".`
          : `✅ Estilo "${estilo.nombre}" aplicado a "${nombreDestinoActual}".`
      );
    } catch (err) {
      console.error("Error aplicando estilo visual:", err);
      alert("❌ No se pudo aplicar el estilo");
    } finally {
      setAplicandoEstilo(null);
    }
  };

  const esElTemaActivo = (tema) =>
    actual &&
    actual.fondo_app?.toLowerCase() === tema.fondo_app.toLowerCase() &&
    actual.fondo_difuminado === tema.fondo_difuminado &&
    actual.color_base?.toLowerCase() === tema.color_base.toLowerCase();

  const aplicarTema = async (tema) => {
    const esDesactivar = tema.key === "ninguno";
    if (
      !window.confirm(
        esDesactivar
          ? `¿Desactivar el tema actual y volver a los colores originales del sistema (blanco / azul institucional)? ` +
              `Esto se aplica a todo el sistema de padres, incluyendo cada servicio.`
          : `¿Aplicar el tema "${tema.nombre}" a TODO el sistema de padres — incluyendo el fondo, ` +
              `encabezado, y el color de cada servicio (calendario y botones dentro de Colegio, CDA, ` +
              `Kindergarten, etc.)? Esto reemplaza cualquier color que hayas personalizado por separado.`
      )
    )
      return;

    try {
      setAplicando(tema.key);

      // 1. Fondo general (pantalla "Seleccione un Servicio")
      await axios.post(`${API_URL}/api/color-fondo`, {
        fondo_app: tema.fondo_app,
        fondo_difuminado: tema.fondo_difuminado,
      });

      // 2. Encabezado / pie de página / menú admin
      await axios.post(`${API_URL}/api/colores-sitio`, {
        color_base: tema.color_base,
        color_header: null,
        color_footer: null,
        color_sidebar: null,
        header_difuminado: tema.header_difuminado,
      });

      // 3. Cada servicio (Colegio, CDA, Kindergarten, etc.) — su propio
      //    fondo Y su color de acento (botones, calendario) — si no se
      //    actualiza esto, cada servicio se queda con su color viejo.
      const resServicios = await axios.get(`${API_URL}/api/servicios`);
      await Promise.all(
        (resServicios.data || []).flatMap((servicio) => [
          axios.post(`${API_URL}/api/servicios/${servicio.id}/color`, {
            color: tema.color_base,
            color_difuminado: tema.fondo_difuminado,
          }),
          axios.post(`${API_URL}/api/servicios/${servicio.id}/color_fondo`, {
            color_fondo: tema.fondo_app,
            fondo_difuminado: tema.fondo_difuminado,
          }),
        ])
      );

      // 4. Secciones de CDA (Kinder / Primaria / Secundaria)
      const resNiveles = await axios.get(`${API_URL}/api/niveles`);
      await Promise.all(
        (resNiveles.data || []).map((nivel) =>
          axios.post(`${API_URL}/api/niveles/${nivel.id}/color`, {
            color: tema.color_base,
            color_difuminado: tema.fondo_difuminado,
          })
        )
      );

      if (setColorFondoGlobal) {
        setColorFondoGlobal(armarFondo(tema.fondo_app, tema.fondo_difuminado));
      }

      await cargarActual();
      alert(
        esDesactivar
          ? "✅ Tema desactivado — el sistema de padres volvió a los colores originales."
          : `✅ Tema "${tema.nombre}" aplicado a todo el sistema de padres — fondo general, cada ` +
              `servicio, y sus calendarios/botones. Los textos se adaptan automáticamente para que siempre se puedan leer.`
      );
    } catch (err) {
      console.error("Error aplicando tema:", err);
      alert("❌ No se pudo aplicar el tema completo. Revisa la consola.");
    } finally {
      setAplicando(null);
    }
  };

  return (
    <div className="p-3 md:p-6 md:max-w-4xl">
      <h3 className="text-xl md:text-2xl font-bold mb-2 text-center md:text-left">
        🎨 Temas Predefinidos
      </h3>
      <p className="text-sm text-gray-500 mb-3 -mt-1">
        Elige un tema profesional y se aplica de una sola vez a todo el sistema de padres
        (fondo, encabezado, pie de página y menú). Los colores de texto se calculan
        automáticamente según cada fondo, así siempre son legibles — no hace falta
        configurar nada más por separado.
      </p>

      <button
        onClick={() => aplicarTema(TEMA_NINGUNO)}
        disabled={aplicando === TEMA_NINGUNO.key}
        className="text-sm text-gray-500 underline hover:text-gray-700 mb-6"
      >
        {aplicando === TEMA_NINGUNO.key ? "Desactivando..." : "🚫 Desactivar tema (volver a colores originales)"}
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {catalogoTemas.map((tema) => {
          const fondoPreview = armarFondo(tema.fondo_app, tema.fondo_difuminado);
          const textoPreview = colorTextoLegible(fondoPreview);
          const activo = esElTemaActivo(tema);

          return (
            <div
              key={tema.key}
              className="rounded-2xl shadow-md border overflow-hidden flex flex-col"
              style={{ borderColor: activo ? tema.color_base : "#e5e7eb", borderWidth: activo ? 2 : 1 }}
            >
              {/* VISTA PREVIA */}
              <div
                style={{ background: fondoPreview, padding: "26px 18px", position: "relative" }}
              >
                <div
                  style={{
                    background: tema.color_base,
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: colorTextoLegible(tema.color_base),
                    display: "inline-block",
                    marginBottom: 14,
                  }}
                >
                  Sistema de Citas
                </div>
                <p style={{ color: textoPreview, fontWeight: 700, fontSize: 15, margin: 0 }}>
                  Seleccione un Servicio
                </p>
                <p style={{ color: textoPreview, fontSize: 12, opacity: 0.85, margin: "4px 0 0" }}>
                  Así se ve el texto sobre este fondo
                </p>

                {activo && (
                  <span
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      background: "#16a34a",
                      color: "white",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "4px 8px",
                      borderRadius: 999,
                    }}
                  >
                    ✓ ACTIVO
                  </span>
                )}
              </div>

              {/* INFO */}
              <div className="p-4 flex flex-col flex-1">
                <strong className="text-slate-800 mb-1">{tema.nombre}</strong>
                <p className="text-xs text-gray-500 mb-4 flex-1">{tema.descripcion}</p>

                <button
                  onClick={() => aplicarTema(tema)}
                  disabled={activo || aplicando === tema.key}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    activo
                      ? "bg-green-100 text-green-700 cursor-default"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {activo ? "Tema activo" : aplicando === tema.key ? "Aplicando..." : "Aplicar este tema"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= ESTILO VISUAL ================= */}
      <h3 className="text-xl md:text-2xl font-bold mb-2 mt-10 text-center md:text-left">
        🖼️ Estilo Visual
      </h3>
      <p className="text-sm text-gray-500 mb-3 -mt-1">
        Independiente del color: define la forma de las tarjetas, los botones, y cómo se
        muestra la lista de docentes (en cuadrícula o en filas tipo lista). No cambia nada
        del funcionamiento, solo cómo se ve. Puedes aplicarlo a todos los servicios a la vez,
        o probarlo primero en uno solo.
      </p>

      {/* SELECTOR DE DESTINO */}
      <div className="flex flex-wrap items-center gap-3 mb-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        <span className="text-sm font-semibold text-indigo-900">🎯 Aplicar a:</span>
        <select
          value={destinoEstilo}
          onChange={(e) => setDestinoEstilo(e.target.value)}
          className="border rounded px-3 py-1.5 bg-white"
        >
          <option value="general">🌐 General (respaldo de los servicios sin estilo propio)</option>
          <option value="pantalla_servicios">🏠 Pantalla de Selección de Servicio</option>
          {servicios.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nombre}
              {s.estilo_visual ? ` — ${catalogoEstilos.find((e) => e.key === s.estilo_visual)?.nombre || s.estilo_visual}` : ""}
            </option>
          ))}
        </select>

        {destinoEstilo === "general" && (
          <span className="text-xs text-gray-500">
            No afecta la pantalla donde el padre elige el servicio — solo es lo que heredan
            los servicios (Colegio, CDA, Kindergarten...) que no tengan un estilo propio.
          </span>
        )}
        {destinoEstilo === "pantalla_servicios" && (
          <span className="text-xs text-gray-500">
            Solo cambia la pantalla inicial donde el padre elige el servicio — independiente
            de lo que uses en cada servicio o en el general.
          </span>
        )}
        {destinoEstilo !== "general" && destinoEstilo !== "pantalla_servicios" && !estiloActivoDestino && (
          <span className="text-xs text-gray-500">
            Este servicio no tiene estilo propio — usa el general ({catalogoEstilos.find((e) => e.key === estiloGeneral)?.nombre || estiloGeneral}).
          </span>
        )}
      </div>

      <button
        onClick={() => aplicarEstilo(ESTILO_NINGUNO)}
        disabled={aplicandoEstilo === ESTILO_NINGUNO.key || !estiloActivoDestino}
        className="text-sm text-gray-500 underline hover:text-gray-700 mb-6 disabled:opacity-40 disabled:no-underline"
      >
        {aplicandoEstilo === ESTILO_NINGUNO.key
          ? "Quitando..."
          : destinoEstilo === "general"
          ? "🚫 Desactivar estilo general (volver a Clásico)"
          : destinoEstilo === "pantalla_servicios"
          ? "🚫 Volver la pantalla de servicios a Clásico"
          : "🚫 Quitar estilo propio de este servicio"}
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {catalogoEstilos.map((estilo) => {
          const activo = estiloActivoDestino === estilo.key;
          const botonPreviewStyle =
            estilo.botonEstilo === "outline"
              ? { background: "white", color: "#1e40af", border: "2px solid #1e40af" }
              : { background: "#1e40af", color: "white", border: "none" };

          return (
            <div
              key={estilo.key}
              className="rounded-2xl shadow-md border overflow-hidden flex flex-col"
              style={{ borderColor: activo ? "#1e40af" : "#e5e7eb", borderWidth: activo ? 2 : 1 }}
            >
              {/* VISTA PREVIA EN MINIATURA */}
              <div style={{ background: "#f1f5f9", padding: 18, position: "relative" }}>
                {activo && (
                  <span
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      background: "#16a34a",
                      color: "white",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "4px 8px",
                      borderRadius: 999,
                    }}
                  >
                    ✓ ACTIVO
                  </span>
                )}

                {estilo.layoutDocentes === "lista" ? (
                  // Mini vista tipo lista de filas
                  <div className="flex flex-col gap-2">
                    {[1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          background: "white",
                          borderRadius: estilo.cardRadius,
                          boxShadow: estilo.cardShadow,
                          border: estilo.cardBorder,
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#cbd5e1" }} />
                        <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#e2e8f0" }} />
                        <div
                          style={{
                            ...botonPreviewStyle,
                            borderRadius: estilo.botonRadius,
                            fontSize: 9,
                            padding: "4px 10px",
                            fontWeight: 700,
                          }}
                        >
                          Elegir
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Mini vista tipo tarjeta
                  <div className="flex gap-3">
                    {[1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          background: "white",
                          borderRadius: estilo.cardRadius,
                          boxShadow: estilo.cardShadow,
                          border: estilo.cardBorder,
                          padding: 12,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#cbd5e1" }} />
                        <div style={{ width: "70%", height: 6, borderRadius: 3, background: "#e2e8f0" }} />
                        <div
                          style={{
                            ...botonPreviewStyle,
                            borderRadius: estilo.botonRadius,
                            fontSize: 9,
                            padding: "4px 12px",
                            fontWeight: 700,
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          Ver
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* INFO */}
              <div className="p-4 flex flex-col flex-1">
                <strong className="text-slate-800 mb-1">{estilo.nombre}</strong>
                <p className="text-xs text-gray-500 mb-4 flex-1">{estilo.descripcion}</p>

                <button
                  onClick={() => aplicarEstilo(estilo)}
                  disabled={activo || aplicandoEstilo === estilo.key}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    activo
                      ? "bg-green-100 text-green-700 cursor-default"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {activo ? "Estilo activo" : aplicandoEstilo === estilo.key ? "Aplicando..." : "Aplicar este estilo"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GestionarTemas;
